import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_APP_THEME,
  getAppTheme,
  isAppThemeId,
  normalizeLegacyThemeId,
  type AppThemeDefinition,
  type AppThemeId,
} from '@/config/appThemes';

const STORAGE_KEY = 'appTheme';
const LEGACY_DARK_KEY = 'darkMode';

function readStoredTheme(): AppThemeId | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isAppThemeId(stored)) return stored;
  } catch {
    /* ignore */
  }
  return null;
}

function migrateLegacyDarkMode(): AppThemeId {
  try {
    const legacy = localStorage.getItem(LEGACY_DARK_KEY);
    if (legacy === 'true' || legacy === 'false') {
      localStorage.setItem(STORAGE_KEY, DEFAULT_APP_THEME);
      localStorage.removeItem(LEGACY_DARK_KEY);
      return DEFAULT_APP_THEME;
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_APP_THEME;
}

export function getInitialAppTheme(): AppThemeId {
  return readStoredTheme() ?? migrateLegacyDarkMode();
}

export function applyAppTheme(themeId: AppThemeId) {
  const theme = getAppTheme(themeId);
  const root = document.documentElement;
  root.dataset.theme = themeId;
  root.classList.remove('dark');
  root.style.colorScheme = 'dark';

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', theme.themeColorMeta);
  }
}

type AppThemeContextValue = {
  themeId: AppThemeId;
  theme: AppThemeDefinition;
  setTheme: (id: AppThemeId) => void;
};

const AppThemeContext = createContext<AppThemeContextValue | null>(null);

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [themeId, setThemeIdState] = useState<AppThemeId>(getInitialAppTheme);

  useEffect(() => {
    applyAppTheme(themeId);
    try {
      localStorage.setItem(STORAGE_KEY, themeId);
    } catch {
      /* ignore */
    }
  }, [themeId]);

  const setTheme = useCallback((id: AppThemeId) => {
    setThemeIdState(id);
  }, []);

  const value = useMemo(
    () => ({
      themeId,
      theme: getAppTheme(themeId),
      setTheme,
    }),
    [themeId, setTheme]
  );

  return <AppThemeContext.Provider value={value}>{children}</AppThemeContext.Provider>;
}

export function useAppTheme(): AppThemeContextValue {
  const ctx = useContext(AppThemeContext);
  if (!ctx) {
    throw new Error('useAppTheme must be used within AppThemeProvider');
  }
  return ctx;
}

export { normalizeLegacyThemeId };
